import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { newsCategories, newsList } from "@/mocks/news";
import { formatDate } from "@/lib/content";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function NewsPage() {
  const [activeTag, setActiveTag] = useState<string>("全部");
  const [order, setOrder] = useState<"desc" | "asc">("desc");

  usePageMeta({
    title: "娱乐资讯 - po18娱乐内容聚合平台",
    description:
      "po18 娱乐资讯栏目，聚合电影动态、剧集更新、明星八卦与行业观察等最新娱乐新闻，助你第一时间掌握影视圈动态。",
    keywords: "po18,娱乐资讯,电影动态,剧集更新,明星八卦,行业观察",
  });

  const tags = ["全部", ...newsCategories];

  const list = useMemo(() => {
    let items = [...newsList];
    if (activeTag !== "全部") {
      items = items.filter((item) => item.category === activeTag);
    }
    items.sort((a, b) =>
      order === "desc" ? (a.date < b.date ? 1 : -1) : a.date < b.date ? -1 : 1,
    );
    return items;
  }, [activeTag, order]);

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-6">
        <nav className="pt-6 flex items-center gap-1.5 text-sm text-foreground-500">
          <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-800 font-medium">娱乐资讯</span>
        </nav>

        <header className="mt-4 rounded-lg bg-gradient-to-r from-accent-50 to-background-100 border border-background-200/70 p-6 md:p-8">
          <h1 className="flex items-center gap-3 text-2xl md:text-3xl font-black text-foreground-950">
            <span className="w-10 h-10 rounded-md bg-accent-500 text-background-50 flex items-center justify-center">
              <i className="ri-newspaper-line text-xl" />
            </span>
            娱乐资讯
          </h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
            聚合电影动态、剧集更新、明星八卦与行业观察，第一时间掌握影视圈的每一个热点，读懂娱乐行业背后的故事。
          </p>
        </header>

        {/* 标签筛选 + 排序 */}
        <div className="mt-6 rounded-lg bg-background-100 border border-background-200/70 p-4 md:p-5 flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex flex-wrap items-center gap-2 flex-1">
            {tags.map((tag) => {
              const active = tag === activeTag;
              return (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setActiveTag(tag)}
                  className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    active
                      ? "bg-accent-500 text-background-50"
                      : "bg-background-200/70 text-foreground-700 hover:bg-accent-100 hover:text-accent-800"
                  }`}
                >
                  {tag}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-sm text-foreground-500 whitespace-nowrap">排序</span>
            <button
              type="button"
              onClick={() => setOrder((o) => (o === "desc" ? "asc" : "desc"))}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-background-50 border border-background-200 text-sm font-medium text-foreground-700 hover:border-primary-300 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className={order === "desc" ? "ri-sort-desc" : "ri-sort-asc"} />
              {order === "desc" ? "最新优先" : "最早优先"}
            </button>
          </div>
        </div>

        <p className="mt-4 text-sm text-foreground-500">
          共 <span className="text-accent-600 font-semibold">{list.length}</span> 条资讯
        </p>

        {list.length === 0 ? (
          <div className="mt-4 rounded-lg bg-background-100 border border-background-200/70 py-20 text-center">
            <span className="w-16 h-16 mx-auto rounded-full bg-background-200 text-foreground-400 flex items-center justify-center text-3xl">
              <i className="ri-inbox-line" />
            </span>
            <h3 className="mt-5 text-lg font-bold text-foreground-900">
              该栏目暂无资讯
            </h3>
            <button
              type="button"
              onClick={() => setActiveTag("全部")}
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line text-lg" />
              查看全部资讯
            </button>
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            {list.map((item) => (
              <Link
                key={item.id}
                to={`/news/${item.id}`}
                className="group block cursor-pointer rounded-lg bg-background-100 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-colors"
              >
                <article className="flex flex-col sm:flex-row gap-4 p-4 md:p-5">
                  <div className="w-full sm:w-52 lg:w-60 shrink-0 aspect-[4/3] rounded-md overflow-hidden bg-background-200">
                    <img
                      src={item.image}
                      alt={`${item.title} 配图`}
                      title={`${item.title} - po18 娱乐资讯`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="min-w-0 flex-1 flex flex-col">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-800 font-medium">
                        {item.category}
                      </span>
                      <span className="text-foreground-400">{formatDate(item.date)}</span>
                    </div>
                    <h2 className="mt-2 text-base md:text-lg font-bold text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
                      {item.title}
                    </h2>
                    <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">
                      {item.summary}
                    </p>
                    <span className="mt-auto pt-3 inline-flex items-center gap-1 text-sm font-medium text-primary-600">
                      阅读全文
                      <i className="ri-arrow-right-line" />
                    </span>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        )}
      </div>
    </SiteLayout>
  );
}